import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Banknote, Save, Loader2, ArrowRightLeft } from 'lucide-react';
import api from '@/lib/api';

export default function AdminPaymentSettings() {
    const [config, setConfig] = useState(null);
    const [methods, setMethods] = useState({ mercadopago: false, cash: false, transfer: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testToken, setTestToken] = useState('');
    const [testPublicKey, setTestPublicKey] = useState('');
    const [testActive, setTestActive] = useState(false);
    const [prodToken, setProdToken] = useState('');
    const [prodPublicKey, setProdPublicKey] = useState('');
    const [prodActive, setProdActive] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/payment-configs');
            const data = res.data.data;
            setConfig(data);
            setMethods(data.methods || { mercadopago: false, cash: false, transfer: false });

            if (data.mercadopago_test) {
                setTestToken(data.mercadopago_test.access_token || '');
                setTestPublicKey(data.mercadopago_test.public_key || '');
                setTestActive(data.mercadopago_test.is_active || false);
            }
            if (data.mercadopago_production) {
                setProdToken(data.mercadopago_production.access_token || '');
                setProdPublicKey(data.mercadopago_production.public_key || '');
                setProdActive(data.mercadopago_production.is_active || false);
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const saveMpConfig = async (environment) => {
        setSaving(true);
        try {
            const payload = environment === 'test'
                ? { environment: 'test', access_token: testToken, public_key: testPublicKey, is_active: testActive }
                : { environment: 'production', access_token: prodToken, public_key: prodPublicKey, is_active: prodActive };

            await api.post('/admin/payment-configs', payload);
        } catch {
            // silent
        } finally {
            setSaving(false);
        }
    };

    const toggleMethod = async (method, enabled) => {
        try {
            await api.put(`/admin/payment-methods/${method}`, { is_enabled: enabled });
            setMethods(prev => ({ ...prev, [method]: enabled }));
        } catch {
            // silent
        }
    };

    const renderMpForm = (env, token, setToken, publicKey, setPublicKey, active, setActive) => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white uppercase tracking-wider">{env === 'test' ? 'Sandbox (Test)' : 'Producción'}</span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-white/60">Activo</span>
                    <Switch checked={active} onCheckedChange={setActive} />
                </div>
            </div>
            <div className="space-y-3">
                <div>
                    <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Access Token</label>
                    <Input
                        type="password"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder={`Access Token ${env === 'test' ? 'de prueba' : 'de producción'}...`}
                        className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                    />
                </div>
                <div>
                    <label className="text-xs text-white/60 uppercase tracking-wider block mb-1.5">Public Key</label>
                    <Input
                        type="password"
                        value={publicKey}
                        onChange={(e) => setPublicKey(e.target.value)}
                        placeholder={`Public Key ${env === 'test' ? 'de prueba' : 'de producción'}...`}
                        className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                    />
                </div>
            </div>
            <Button
                onClick={() => saveMpConfig(env)}
                disabled={saving}
                className="w-full uppercase tracking-widest text-xs"
            >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Guardar {env === 'test' ? 'Test' : 'Producción'}
            </Button>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide">Configuración de Pagos</h1>
                <p className="text-white/60 text-sm mt-1">Administra las credenciales de MercadoPago y los métodos de pago disponibles para todo el sitio.</p>
            </div>

            {/* Payment Methods */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Métodos de Pago
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-white/80" />
                            <div>
                                <p className="text-sm font-medium text-white">MercadoPago</p>
                                <p className="text-xs text-white/60">Pagos online con tarjeta de crédito/débito</p>
                            </div>
                        </div>
                        <Switch
                            checked={methods.mercadopago}
                            onCheckedChange={(v) => toggleMethod('mercadopago', v)}
                        />
                    </div>
                    <Separator className="bg-white/10" />
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <Banknote className="h-5 w-5 text-white/80" />
                            <div>
                                <p className="text-sm font-medium text-white">Pago en Efectivo</p>
                                <p className="text-xs text-white/60">Pagos presenciales en el gimnasio</p>
                            </div>
                        </div>
                        <Switch
                            checked={methods.cash}
                            onCheckedChange={(v) => toggleMethod('cash', v)}
                        />
                    </div>
                    <Separator className="bg-white/10" />
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <ArrowRightLeft className="h-5 w-5 text-white/80" />
                            <div>
                                <p className="text-sm font-medium text-white">Transferencia Bancaria</p>
                                <p className="text-xs text-white/60">Pago por transferencia bancaria</p>
                            </div>
                        </div>
                        <Switch
                            checked={methods.transfer}
                            onCheckedChange={(v) => toggleMethod('transfer', v)}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* MercadoPago Credentials */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Credenciales de MercadoPago
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="p-4 border border-hairline-dark rounded-lg space-y-4">
                        <Badge variant="secondary" className="uppercase tracking-wider text-xs">Sandbox</Badge>
                        {renderMpForm('test', testToken, setTestToken, testPublicKey, setTestPublicKey, testActive, setTestActive)}
                    </div>
                    <div className="p-4 border border-hairline-dark rounded-lg space-y-4">
                        <Badge variant="default" className="uppercase tracking-wider text-xs bg-white text-black">Producción</Badge>
                        {renderMpForm('production', prodToken, setProdToken, prodPublicKey, setProdPublicKey, prodActive, setProdActive)}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
